import React, { useContext, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface MoodImpairmentFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const MoodImpairmentForm: React.FC<MoodImpairmentFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [showSubmit, setShowSubmit] = useState(false);

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const {
    isLoading: isLoadingMoodImpairmentMutation,
    mutate: submitMoodImpairmentForm
  } = useMutation<string>(
    "add-bill",
    async () => {
      return await createApiClient().addMoodImpairmentFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        mood: localStorage.getItem("hasHadMoodChanges") || "",
        lack_interest: localStorage.getItem("lackOfInterest") || "",
        feeling_down: localStorage.getItem("feelingDown") || "",
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasHadMoodChanges") === "No") {
      form.resetFields(["Lack of interest", "Feeling down"]);
      localStorage.setItem("lackOfInterest", "");
      localStorage.setItem("feelingDown", "");
      submitMoodImpairmentForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      submitMoodImpairmentForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Any mood changes in last month"]);
    } else {
      form.resetFields(["Lack of interest", "Feeling down"]);
    }
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="moodImpairment"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you been bothered by any mood changes in the last one month?"
                required
              >
                <FormItemStyle
                  name="Any mood changes in last month"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("hasHadMoodChanges")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem("hasHadMoodChanges", e.target.value);
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <>
              <SurveyQuestionCard
                title="Lack of interest or pleasure in doing things"
                required
              >
                <FormItemStyle
                  name="Lack of interest"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("lackOfInterest")}
                    onChange={e =>
                      localStorage.setItem("lackOfInterest", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Not bothered at all">
                          Not bothered at all
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Rarely (1 - 3 times)">
                          Rarely (1 - 3 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Occasionally (4 - 7 times)">
                          Occasionally (4 - 7 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Frequently (8 - 15 times)">
                          Frequently (8 - 15 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Nearly everyday">Nearly everyday</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="Feeling down, depressed, or hopeless"
                required
              >
                <FormItemStyle
                  name="Feeling down"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("feelingDown")}
                    onChange={e =>
                      localStorage.setItem("feelingDown", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Not bothered at all">
                          Not bothered at all
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Rarely (1 - 3 times)">
                          Rarely (1 - 3 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Occasionally (4 - 7 times)">
                          Occasionally (4 - 7 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Frequently (8 - 15 times)">
                          Frequently (8 - 15 times)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Nearly everyday">Nearly everyday</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage !== 1 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingMoodImpairmentMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
